import type { BootServices } from '../../bootstrap';
import type { Command, Scenario } from './types';
import {
  allCommands,
  availableCommands,
  getOnceForCommand,
  getTransitionForCommand,
  indexScenario,
  pickStateVideo,
  type ScenarioIndex,
} from './scenario';
import type { DeductResult } from './credits';
import { executePlayerCommand, listPlayerEntitlements, recordCommand } from './credits';
import { Billing } from './billing';
import { persistStateId, restoreStateId } from './persistence';
import { mseManifestUrlForMp4 } from './mse/manifest';
import { MseEngine } from './mse/engine';
import { PlayerLogger, type PlayerBreadcrumb } from './debug/logger';
import { captureError } from '../../monitoring/errors';
import { requestAuthRequired } from '../../utils/telegramUser';
import { PlayerIncidentTracker } from './monitoring/playerIncidentTracker';
import { apiJsonOrThrow, authedFetchJson } from '../../utils/api';
import { fetchPublicAsset } from '../../utils/publicFetch';
import { toThumbUrl } from '../../utils/thumbUrl';

// Track quest progress (best-effort, fire-and-forget)
async function trackQuestProgress(services: BootServices, actionType: string, delta = 1) {
  try {
    await authedFetchJson(services, '/api/quests/progress', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action_type: actionType, delta }),
    });
  } catch {
    // Ignore errors - quest tracking is best-effort
  }
}

export type PaywallState =
  | { open: false }
  | {
      open: true;
      balance: number | null;
      needed: number;
      commandName: string;
      reason: 'insufficient_credits' | 'premium_required' | 'private_show_required';
      private_show?: any;
    };

type UiCommandContext = {
  commandEventId?: string;
  displayText?: string;
  uiLocale?: string;
  onRunId?: (runId: string) => void;
};

type LifecycleResumeOptions = {
  forceRecover?: boolean;
  verifyPlaybackMs?: number;
};

export type ChatPlayerState = {
  modelId: number;
  modelName: string;
  avatarUrl: string | null;
  hasPersonalTelegramBot: boolean;
  personalTelegramBotUsername: string | null;
  scenario: Scenario;
  idx: ScenarioIndex;
  currentStateId: string;
  commands: Command[];
  purchasedCommands: string[];
  isBusy: boolean;
  isResetting: boolean;
  paywall: PaywallState;
  // Model of the Day support
  isModelOfDay: boolean;
  modelOfDayCommandCost: number | null;
};

export type ChatPlayerDeps = {
  services: BootServices;
  modelId: number;
  chatSessionId: string;
  videoA: HTMLVideoElement;
  videoB: HTMLVideoElement;
  onState: (s: ChatPlayerState) => void;
  locale: () => string;
};

export type ModelMeta = {
  id: number;
  name: string;
  imageUrl: string | null;
  hasPersonalTelegramBot: boolean;
  personalTelegramBotUsername: string | null;
  scenarioFile: string;
  isModelOfDay: boolean;
  modelOfDayCommandCost: number | null;
};

function parseModelMeta(data: any, modelId: number): ModelMeta {
  return {
    id: Number(data?.id || modelId),
    name: String(data?.name || `Model ${modelId}`),
    imageUrl: data?.image_url ? String(data.image_url) : null,
    hasPersonalTelegramBot: Boolean(data?.has_personal_telegram_bot),
    personalTelegramBotUsername: data?.personal_telegram_bot_username ? String(data.personal_telegram_bot_username) : null,
    scenarioFile: data?.scenario_file ? String(data.scenario_file) : `models/model_${modelId}.json`,
    isModelOfDay: Boolean(data?.is_model_of_day),
    modelOfDayCommandCost: data?.model_of_day_command_cost != null ? Number(data.model_of_day_command_cost) : null,
  };
}

async function loadModelMeta(services: BootServices, modelId: number): Promise<ModelMeta> {
  const out: any = await apiJsonOrThrow(`/api/models/${encodeURIComponent(String(modelId))}/meta`, { method: 'GET' }, {
    feature: 'chat_player',
    stage: 'load_model_meta',
    modelId,
  });
  return parseModelMeta(out?.data, modelId);
}

export { loadModelMeta, parseModelMeta };

async function loadScenarioFile(path: string, env?: string): Promise<Scenario> {
  const raw = String(path || '').trim();
  if (!raw) throw new Error('Scenario path is empty');
  if (/^https?:\/\//i.test(raw)) throw new Error('Scenario path must be relative (no URL)');

  // Canonical: models/... (relative to /scenarios/)
  const rel = raw.replace(/^\/+/, '');
  if (!rel.startsWith('models/')) throw new Error(`Invalid scenario path (expected models/*): ${raw}`);

  const base = `/scenarios/${rel}`;
  const isTest = String(env || '').toLowerCase() === 'test';
  const bust = Boolean(import.meta.env.DEV) || isTest;
  const url = bust ? `${base}${base.includes('?') ? '&' : '?'}t=${Date.now()}` : base;
  const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
  const t = setTimeout(() => { try { ctrl?.abort(); } catch {} }, TUNE.scenarioFetchTimeoutMs);
  let res: Response;
  try {
    res = await fetchPublicAsset(
      url,
      { cache: (bust ? 'no-store' : 'default') as any, signal: (ctrl as any)?.signal },
      { expected_content_type: 'json', feature: 'chat_player', stage: 'load_scenario', scenario_path: rel },
    );
  } catch (e: any) {
    const aborted = String(e?.name || '').toLowerCase().includes('abort');
    throw new Error(aborted ? `Scenario fetch timeout: ${url}` : `Scenario fetch failed: ${url}`);
  } finally {
    try { clearTimeout(t); } catch {}
  }
  const json = (await res.json()) as any;
  if (!json || !Array.isArray(json.blocks)) throw new Error('Invalid scenario format (blocks[])');
  return json as Scenario;
}

function cleanPlaylist(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  for (const x of list) {
    const s = String(x || '').trim();
    if (s) out.push(s);
  }
  return out;
}

function sec(ms: number): number {
  return Math.max(0, Number(ms || 0) / 1000);
}

const TUNE = {
  // Networking
  scenarioFetchTimeoutMs: 15_000,

  // UX
  resetOverlayMinMs: 450,

  // MSE
  mseOpenTimeoutMs: 5_000,

  // Timing tolerances (seconds)
  iterEpsilonSec: 0.05,
  boundaryMinLeadSec: 0.6,
  boundaryFallbackLeadSec: 0.6,
  stateTickFallbackAdvanceSec: 0.6,

  // Buffer policy
  stateMinItersAhead: 2,
  stateNeedMoreMultiplier: 1.2,

  // Telemetry throttling
  telemetryMinGapPlayerMs: 60_000,
  telemetryMinGapOtherMs: 15_000,

  // Monitoring
  startTimeoutMs: 25_000,
  waitTickLogEveryMs: 800,
} as const;

type ActionRunResult =
  | { status: 'ok'; runId?: string | null; spentCost?: number; accessSource?: string | null; privateShowGrantId?: string | null }
  | { status: 'noop' }
  | { status: 'blocked'; r: DeductResult; needed: number; commandName: string; runId?: string | null }
  | { status: 'error'; err: string; meta?: Record<string, any>; cause?: unknown };

export type ExecuteResult = ActionRunResult;

export class ChatPlayerController {
  private deps: ChatPlayerDeps;
  private state: ChatPlayerState | null = null;
  private destroyed = false;
  private billing = new Billing();

  private video: HTMLVideoElement;
  private mse: MseEngine;
  private inAction = false;
  private logger = new PlayerLogger(300);

  private removeVideoMonitors: Array<() => void> = [];
  private startTimeoutTimer: any = null;
  private lastReportedAt: Record<string, number> = {};
  private incidentTracker: PlayerIncidentTracker | null = null;
  private lifecycleResumePromise: Promise<void> | null = null;
  private lastLifecycleResumeAt = 0;
  private lastLifecycleHardRecoverAt = 0;

  private stateManifestUrl: string | null = null;
  private stateVideoCandidates: string[] = [];
  private lastStateMp4: string | null = null;
  private stateDurEstimateSec = 0;
  private stateIterEnd = 0;
  private stateIterEnds: number[] = [];
  private stateIterIndex = 0;
  private stateTickRunning = false;
  private stateTickPending = false;

  // Single interface for logging + error reporting.
  // (Use via this.log()/this.reportOnce() wrappers to keep callsites tidy.)
  private telemetry = {
    event: (tag: string, data?: unknown) => {
      try { this.logger.log(tag, data); } catch {}
    },
    error: (type: string, message: string, meta?: Record<string, any>, severity: 'info' | 'warning' | 'error' = 'error') => {
      try {
        const player: Record<string, any> = {};
        try {
          const m = meta || {};
          if (m.readyState !== undefined) player.ready_state = m.readyState;
          if (m.networkState !== undefined) player.network_state = m.networkState;
          if (m.currentTime !== undefined) player.current_time = m.currentTime;
          if (m.bufferedEnd !== undefined) player.buffered_end = m.bufferedEnd;
          if (m.currentSrc !== undefined) player.current_src = m.currentSrc;
          if (m.code !== undefined) player.error_code = m.code;
        } catch {}
        captureError({
          type,
          message,
          severity,
          modelId: this.state?.modelId ?? this.deps.modelId,
          chatSessionId: this.deps?.chatSessionId,
          player: Object.keys(player).length ? player : null,
          meta: {
            modelId: this.state?.modelId ?? this.deps.modelId,
            chatSessionId: this.deps?.chatSessionId,
            env: this.deps?.services?.config?.env,
            ...meta,
          },
        });
      } catch {}
    },
    errorOnce: (type: string, message: string, meta?: Record<string, any>, severity: 'info' | 'warning' | 'error' = 'error') => {
      try {
        const now = Date.now();
        const last = Number(this.lastReportedAt[type] || 0);
        const minGapMs = (type.includes('video_') || type.includes('player_')) ? TUNE.telemetryMinGapPlayerMs : TUNE.telemetryMinGapOtherMs;
        if (now - last < minGapMs) return;
        this.lastReportedAt[type] = now;
        this.telemetry.error(type, message, meta, severity);
      } catch {}
    },
  };

  // ======= State video candidates (single source of truth) =======
  private getStateBlock(stateId: string) {
    if (!this.state) return null;
    return this.state.idx.statesById.get(String(stateId)) || null;
  }

  private getStateCandidates(stateId: string): string[] {
    const st = this.getStateBlock(stateId);
    return Array.isArray((st as any)?.videoUrls) && (st as any).videoUrls.length
      ? (st as any).videoUrls.map((u: any) => String(u || '').trim()).filter(Boolean)
      : (st && (st as any).videoUrl ? [String((st as any).videoUrl).trim()].filter(Boolean) : []);
  }

  private getPrefetchMp4ForState(stateId: string): string {
    const candidates = this.getStateCandidates(stateId);
    // v4: no selected video; treat candidates as variants and prefetch the first available.
    return candidates[0] || '';
  }

  // ======= Action clip variants (transition / once) =======
  // For both transition and once blocks:
  // - videoUrls[] is treated as VARIANTS (not a playlist)
  // - we pick ONE random clip to play for the action
  private listActionVariants(block: { videoUrl?: unknown; videoUrls?: unknown } | null | undefined): string[] {
    const b: any = block as any;
    return cleanPlaylist(
      Array.isArray(b?.videoUrls) && b.videoUrls.length
        ? b.videoUrls
        : (b?.videoUrl ? [b.videoUrl] : []),
    );
  }

  private pickRandomActionVariant(block: { videoUrl?: unknown; videoUrls?: unknown } | null | undefined): { variants: string[]; chosen: string | null } {
    const variants = this.listActionVariants(block);
    if (variants.length === 0) return { variants, chosen: null };
    const chosen = variants[Math.floor(Math.random() * variants.length)] || variants[0] || null;
    return { variants, chosen: chosen || null };
  }

  private prefetchFirstActionVariant(block: { videoUrl?: unknown; videoUrls?: unknown } | null | undefined) {
    const variants = this.listActionVariants(block);
    const mp4 = variants[0] || '';
    if (!mp4) return;
    const mu = mseManifestUrlForMp4(mp4);
    if (!mu) return;
    void this.mse.prefetchClip(mu).catch((e) => this.log('prefetch.error', { mu, err: String(e) }));
  }

  // ======= Action billing (transition / once) =======
  private async chargeForAction(opts: {
    kind: 'transition' | 'once';
    commandName: string;
    modelId: number;
    cost: number;
    premiumOnly?: boolean;
    ui?: UiCommandContext;
  }): Promise<
    | { ok: true; runId?: string | null; spentCost: number; accessSource?: string | null; privateShowGrantId?: string | null }
    | { ok: false; r: DeductResult; needed: number; runId?: string | null }
  > {
    const kind = opts.kind;
    const commandName = String(opts.commandName || '').trim();
    const modelId = Number(opts.modelId || 0);
    const cost = Number(opts.cost || 0);
    const purchaseKey = `${kind}:${commandName}`;
    const commandEventId = (opts?.ui?.commandEventId && String(opts.ui.commandEventId).trim())
      ? String(opts.ui.commandEventId).trim()
      : (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : undefined);

    // Server-authoritative: resolves cost/premiumOnly from scenario and checks entitlement (cross-device).
    const stateId = this.state?.currentStateId || '';
    const rExec = await executePlayerCommand({
      services: this.deps.services,
      modelId,
      kind,
      stateId,
      commandName,
      displayText: opts?.ui?.displayText,
      uiLocale: opts?.ui?.uiLocale,
      chatSessionId: this.deps.chatSessionId,
      commandEventId,
    });
    if (rExec && (rExec as any).runId && typeof opts?.ui?.onRunId === 'function') {
      try { opts.ui.onRunId(String((rExec as any).runId)); } catch {}
    }
    if (!rExec.ok) {
      if (rExec.reason === 'premium_required') {
        return { ok: false, r: { ok: false, reason: 'premium_required', runId: rExec.runId ?? null } as any, needed: 0, runId: rExec.runId ?? null };
      }
      if (rExec.reason === 'insufficient') {
        return {
          ok: false,
          r: { ok: false, reason: 'insufficient', balance: rExec.balance ?? 0, runId: rExec.runId ?? null } as any,
          needed: Math.max(0, Number(rExec.required ?? cost) || 0),
          runId: rExec.runId ?? null,
        };
      }
      if (rExec.reason === 'private_show_required') {
        return {
          ok: false,
          r: {
            ok: false,
            reason: 'private_show_required',
            runId: rExec.runId ?? null,
            required: Number(rExec.required ?? 0) || 0,
            private_show: rExec.private_show || null,
          } as any,
          needed: Math.max(0, Number(rExec.required ?? 0) || 0),
          runId: rExec.runId ?? null,
        };
      }
      return { ok: false, r: { ok: false, reason: rExec.reason || 'network', runId: rExec.runId ?? null } as any, needed: Math.max(0, cost || 0), runId: rExec.runId ?? null };
    }

    // Cache entitlements locally for UX (but server remains source of truth).
    const shouldUnlock = (rExec.charged || rExec.already_owned) && Number(rExec.listed_cost || 0) > 0;
    if (shouldUnlock) {
      try { this.billing.markPurchased(modelId, rExec.commandKey || purchaseKey); } catch {}
    }

    // Track quest progress for chat messages (best-effort, fire-and-forget)
    void trackQuestProgress(this.deps.services, 'chat_messages', 1);

    return {
      ok: true,
      runId: rExec.runId ?? null,
      spentCost: Math.max(0, Number(rExec.spent_cost || 0) || 0),
      accessSource: rExec.access_source || null,
      privateShowGrantId: rExec.private_show_grant_id || null,
    };
  }

  // ======= Shared MSE helpers for actions (transition / once) =======
  private async appendStateTwoIters(opts: {
    tag: 'transition' | 'once';
    candidates: string[];
    appendAt: number;
  }): Promise<null | { endAfter1: number; endAfter2: number; d1: number; d2: number; p2: { mp4: string; mu: string } }> {
    const candidates = Array.isArray(opts.candidates) ? opts.candidates : [];
    if (candidates.length === 0) return null;

    const pick = () => {
      const mp4 = candidates[Math.floor(Math.random() * candidates.length)] || candidates[0];
      const mu = mseManifestUrlForMp4(mp4);
      if (!mu) return null;
      return { mp4, mu };
    };

    const p1 = pick();
    if (!p1) return null;
    const p2 = pick() || p1;
    this.log(`${opts.tag}.appendState.start`, { variants: candidates.length, mu1: p1.mu, mu2: p2.mu, at: opts.appendAt });

    const endBefore = this.mse.bufferedEnd();
    await this.mse.appendClipAt(p1.mu, opts.appendAt);
    const endAfter1 = this.mse.bufferedEnd();
    await this.mse.appendClipAt(p2.mu, endAfter1);
    const endAfter2 = this.mse.bufferedEnd();

    const d1 = Math.max(0, endAfter1 - Math.max(endBefore, opts.appendAt));
    const d2 = Math.max(0, endAfter2 - endAfter1) || d1;
    this.log(`${opts.tag}.appendState.done`, { d1, d2, bufferedEnd: endAfter2 });
    return { endAfter1, endAfter2, d1, d2, p2 };
  }

  private async spliceActionAndEnterTarget(opts: {
    tag: 'transition' | 'once';
    actionManifestUrl: string;
    targetStateId: string;
    targetCandidates: string[];
  }): Promise<{
    boundary: number;
    targetStart: number;
    actionDur: number;
    appended: { endAfter1: number; endAfter2: number; d1: number; d2: number; p2: { mp4: string; mu: string } };
  }> {
    const enrich = (err: unknown, extra?: Record<string, any>) => {
      const e: any = err instanceof Error ? err : new Error(String(err || 'error'));
      const prev = (err as any)?.telemetry && typeof (err as any).telemetry === 'object' ? (err as any).telemetry : null;
      e.telemetry = { ...(prev || {}), ...(extra || {}) };
      return e;
    };
    this.prefetchStateManifest(opts.targetStateId);

    const boundary = this.nextStateBoundary();
    this.log(`${opts.tag}.boundary`, { ct: Number(this.video.currentTime || 0), boundary, bufferedEnd: this.mse.bufferedEnd() });
    try {
      await this.mse.removeFrom(boundary);
    } catch (e) {
      throw enrich(e, { stage: 'remove_from', boundary, tag: opts.tag });
    }
    this.log(`${opts.tag}.removed`, { boundary, bufferedEnd: this.mse.bufferedEnd() });

    // Append action clip
    void this.mse.prefetchClip(opts.actionManifestUrl);
    this.log(`${opts.tag}.appendClip.start`, { actionManifestUrl: opts.actionManifestUrl });
    const endBefore = this.mse.bufferedEnd();
    let m: any;
    try {
      m = await this.mse.appendClipAt(opts.actionManifestUrl, boundary);
    } catch (e) {
      throw enrich(e, { stage: 'append_action', boundary, action_manifest_url: opts.actionManifestUrl, tag: opts.tag });
    }
    const endAfter = this.mse.bufferedEnd();
    const actionDur = Math.max(0, endAfter - endBefore) || sec(m.totalDurationMs);
    const targetStart = boundary + actionDur;
    this.log(`${opts.tag}.appendClip.done`, { actionDur, bufferedEnd: endAfter });

    let appended: any = null;
    try {
      appended = await this.appendStateTwoIters({ tag: opts.tag, candidates: opts.targetCandidates, appendAt: targetStart });
    } catch (e) {
      throw enrich(e, { stage: 'append_target_state', target_start: targetStart, tag: opts.tag });
    }
    if (!appended) throw enrich(new Error('target_state_has_no_videos'), { stage: 'append_target_state', target_start: targetStart, tag: opts.tag });

    const ok = await this.waitUntilOrStall(targetStart - TUNE.iterEpsilonSec);
    if (!ok) throw enrich(new Error('stall'), { stage: 'wait_until', target_start: targetStart, tag: opts.tag });

    return { boundary, targetStart, actionDur, appended };
  }

  constructor(deps: ChatPlayerDeps) {
    this.deps = deps;
    this.video = deps.videoA;
    this.mse = new MseEngine(this.video, {
      onNetRecovered: (stage, meta) => {
        // We only emit warnings when network recovered by itself (retry/resume).
        // This gives us visibility into "bad networks" without polluting error dashboards.
        const type = stage === 'manifest' ? 'player_net_recovered_manifest' : 'player_net_recovered_segment';
        this.telemetry.errorOnce(
          type,
          type,
          { feature: 'chat', stage: `mse_${stage}_recovered`, ...(meta || {}) },
          'warning',
        );
      },
    });
    // We don't use videoB anymore, but keep it paused to reduce decoder pressure.
    try { deps.videoB.pause(); } catch {}

    // Episode-based player incident tracker (event-driven, low overhead).
    this.incidentTracker = new PlayerIncidentTracker({
      video: this.video,
      bufferedEndSec: () => this.mse?.bufferedEnd?.() ?? 0,
      mseImplName: () => (this.mse as any)?.implName?.() ?? null,
      getStageContext: () => ({ stage: 'chat', stateId: this.state?.currentStateId || null, inAction: this.inAction }),
      onIncident: (inc) => {
        this.telemetry.error(
          inc.type,
          inc.message,
          { ...(inc.meta || {}), incident_id: inc.incident_id },
          inc.severity === 'warning' ? 'warning' : 'error',
        );
      },
    });

    // NOTE: Only MSE/MMS mode is supported now.
    this.installVideoMonitoring();
  }

  async init(opts?: { prefetchedMeta?: ModelMeta | null }) {
    const { services, modelId } = this.deps;
    if (this.destroyed) return;
    try {
      this.log('init.start', { modelId });
      const meta = opts?.prefetchedMeta ?? await loadModelMeta(services, modelId);
      if (this.destroyed) return;
      this.log('init.modelMeta', { scenarioFile: meta.scenarioFile, name: meta.name });

      // Start entitlements fetch in parallel with scenario load (best-effort).
      const entitlementsP = listPlayerEntitlements({ services: this.deps.services, modelId })
        .catch((e: any) => {
          try {
            captureError({
              type: 'ui_error',
              message: 'api:chat.entitlements_sync_failed',
              severity: 'warning',
              meta: {
                kind: 'api',
                code: 'chat.entitlements_sync_failed',
                feature: 'chat',
                stage: 'player_init',
                endpoint: '/api/player/entitlements',
                modelId,
                chatSessionId: this.deps.chatSessionId || null,
              },
            });
          } catch {}
          this.log('entitlements.sync.error', { err: String((e as any)?.message || e || '') });
          return null;
        });

      const scenario = await loadScenarioFile(meta.scenarioFile, services.config.env);
      if (this.destroyed) return;
      this.log('init.scenario.loaded', { blocks: Array.isArray((scenario as any)?.blocks) ? (scenario as any).blocks.length : null });

      const idx = indexScenario(scenario);
      const initial = idx.initialStateId;
      if (!initial) throw new Error('Scenario has no state blocks');

      let currentStateId = initial;
      const saved = restoreStateId({ modelId, isValid: (id) => idx.statesById.has(String(id)) });
      if (saved) currentStateId = saved;

      this.billing.loadPurchased(modelId);

      const avatarUrl = toThumbUrl(meta.imageUrl, { cssWidth: 40, cssHeight: 40 });
      this.state = {
        modelId,
        modelName: meta.name,
        avatarUrl: avatarUrl || null,
        hasPersonalTelegramBot: meta.hasPersonalTelegramBot,
        personalTelegramBotUsername: meta.personalTelegramBotUsername,
        scenario,
        idx,
        currentStateId,
        commands: [],
        purchasedCommands: this.billing.listPurchased(),
        isBusy: false,
        isResetting: false,
        paywall: { open: false },
        isModelOfDay: meta.isModelOfDay,
        modelOfDayCommandCost: meta.modelOfDayCommandCost,
      };

      this.refreshCommands();
      this.emit();

      // Preload avatar so it's ready before MSE segments compete for bandwidth.
      if (avatarUrl) {
        try { const img = new Image(); img.src = avatarUrl; } catch {}
      }

      // Apply entitlements that loaded in parallel with scenario.
      const rEnt = await entitlementsP;
      if (rEnt && rEnt.ok && Array.isArray(rEnt.items) && rEnt.items.length) {
        this.billing.replacePurchased(modelId, rEnt.items);
        this.refreshCommands();
        this.emit();
      }

      await this.enterState(currentStateId);
    } catch (e) {
      this.log('init.error', { err: String(e) });
      throw e;
    }
  }

  destroy() {
    this.destroyed = true;
    this.log('destroy');
    this.state = null;
    this.clearStartTimeout();
    try { this.incidentTracker?.destroy(); } catch {}
    this.incidentTracker = null;
    for (const off of this.removeVideoMonitors) {
      try { off(); } catch {}
    }
    this.removeVideoMonitors = [];
    try { this.video.ontimeupdate = null; } catch {}
    try { this.video.onended = null; } catch {}
    try { this.video.onerror = null; } catch {}
    try { this.mse.destroy(); } catch {}
  }

  getState(): ChatPlayerState {
    if (!this.state) throw new Error('Not initialized');
    return this.state;
  }

  // For old debug modal compatibility
  getBreadcrumbs(): PlayerBreadcrumb[] {
    return this.logger.list();
  }

  closePaywall() {
    if (!this.state) return;
    this.state = { ...this.state, paywall: { open: false } };
    this.emit();
  }

  async reset() {
    if (!this.state) return;
    if (this.state.isResetting) return;
    const initial = this.state.idx.initialStateId;
    if (!initial) return;
    const t0 = performance.now();
    // UX: treat reset as a "jingle" transition back to the first state.
    // We disable controls briefly and then re-enter the initial state.
    this.state = { ...this.state, currentStateId: initial, isBusy: true, isResetting: true, paywall: { open: false } };
    this.persistState();
    this.refreshCommands();
    this.emit();
    // On some WebViews removing buffer from 0 while video is mid-play stalls hard.
    // Do a hard MediaSource re-create and restart from time=0.
    try {
      await this.recoverToState(initial);
    } finally {
      // Keep overlay visible at least a tiny bit so it reads as a transition, not a flash.
      const minMs = TUNE.resetOverlayMinMs;
      const dt = performance.now() - t0;
      if (dt < minMs) {
        await new Promise<void>((r) => setTimeout(r, Math.max(0, Math.floor(minMs - dt))));
      }
      if (this.state) {
        this.state = { ...this.state, isBusy: false, isResetting: false };
        this.refreshCommands();
        this.emit();
      }
    }
  }

  async handleLifecycleResume(reason = 'visible', opts?: LifecycleResumeOptions) {
    if (this.destroyed || !this.state) return;
    try {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    } catch {}
    if (this.lifecycleResumePromise) return this.lifecycleResumePromise;
    const now = Date.now();
    const forceRequested = Boolean(opts?.forceRecover);
    if (!forceRequested && now - this.lastLifecycleResumeAt < 750) return;
    this.lastLifecycleResumeAt = now;

    this.lifecycleResumePromise = (async () => {
      if (this.destroyed || !this.state) return;
      const stateId = String(this.state.currentStateId || '');
      const v = this.video;
      const verifyPlaybackMs = Math.max(0, Math.min(3_000, Math.trunc(Number(opts?.verifyPlaybackMs || 0) || 0)));
      let forceRecover = forceRequested;
      if (forceRecover && Date.now() - this.lastLifecycleHardRecoverAt < 8_000) forceRecover = false;
      const snapshot = () => {
        const src = (() => { try { return String((v as any).currentSrc || (v as any).src || ''); } catch { return ''; } })();
        const ct = (() => { try { return Number(v.currentTime || 0) || 0; } catch { return 0; } })();
        const bufferedEnd = (() => { try { return Number(this.mse?.bufferedEnd?.() || 0) || 0; } catch { return 0; } })();
        return {
          reason,
          stateId,
          readyState: (() => { try { return Number((v as any).readyState ?? -1); } catch { return -1; } })(),
          networkState: (() => { try { return Number((v as any).networkState ?? -1); } catch { return -1; } })(),
          paused: (() => { try { return Boolean((v as any).paused); } catch { return null; } })(),
          currentTime: ct,
          bufferedEnd,
          currentSrc: src,
          errorCode: (() => { try { return Number((v as any).error?.code || 0) || null; } catch { return null; } })(),
          videoWidth: (() => { try { return Number((v as any).videoWidth || 0) || 0; } catch { return 0; } })(),
          videoHeight: (() => { try { return Number((v as any).videoHeight || 0) || 0; } catch { return 0; } })(),
          mseImpl: (() => { try { return this.mse?.implName?.() || null; } catch { return null; } })(),
        };
      };

      const before = snapshot();
      this.log('lifecycle.resume', { ...before, forceRecover });
      if (this.inAction || this.state.isBusy || this.state.isResetting) {
        try { await v.play(); this.log('lifecycle.play.busy', snapshot()); } catch {}
        return;
      }

      const src = String(before.currentSrc || '');
      const bufferExhausted = before.bufferedEnd > 0 && before.currentTime >= before.bufferedEnd - 0.15;
      const needsRecovery =
        Boolean(before.errorCode) ||
        !src ||
        !src.startsWith('blob:') ||
        before.readyState <= 0 ||
        before.bufferedEnd <= 0 ||
        before.videoWidth <= 0 ||
        before.videoHeight <= 0 ||
        bufferExhausted;

      if (needsRecovery || forceRecover) {
        const recoverReason = needsRecovery ? 'unhealthy_media' : 'return_refresh';
        this.lastLifecycleHardRecoverAt = Date.now();
        this.log('lifecycle.recover', { ...before, reason: recoverReason, forceRecover });
        await this.recoverToState(stateId);
        return;
      }

      try {
        this.installStateLoopTick();
        const startedAt = snapshot();
        await v.play();
        try { this.incidentTracker?.start(); } catch {}
        if (verifyPlaybackMs > 0) {
          await new Promise<void>((resolve) => setTimeout(resolve, verifyPlaybackMs));
          const after = snapshot();
          const advanced = after.currentTime > startedAt.currentTime + 0.05;
          const hasFutureBuffer = after.bufferedEnd > after.currentTime + 0.15;
          if (!this.destroyed && this.state && !this.inAction && !this.state.isBusy && !advanced && hasFutureBuffer && after.paused === false) {
            this.lastLifecycleHardRecoverAt = Date.now();
            this.log('lifecycle.recover', { ...after, reason: 'playback_not_advancing', verifyPlaybackMs });
            await this.recoverToState(stateId);
            return;
          }
        }
        this.log('lifecycle.play', snapshot());
      } catch (e) {
        this.log('lifecycle.recover', { ...snapshot(), reason: 'play_failed', err: String((e as any)?.message || e || '') });
        this.lastLifecycleHardRecoverAt = Date.now();
        await this.recoverToState(stateId);
      }
    })().finally(() => {
      this.lifecycleResumePromise = null;
    });

    return this.lifecycleResumePromise;
  }

  async execute(cmd: Command, ui?: UiCommandContext): Promise<ExecuteResult> {
    if (!this.state) return { status: 'noop' };
    if (this.state.isResetting) return { status: 'noop' };
    if (!cmd.available) return { status: 'noop' };
    if (this.state.isBusy) return { status: 'noop' };

    this.state = { ...this.state, isBusy: true };
    this.emit();

    const locale = this.deps.locale();
    // NOTE: recordCommand() is handled inside runTransition/runOnce using actualCost (real spent credits).

    try {
      let r: ActionRunResult;
      if (cmd.kind === 'transition') r = await this.runTransition(cmd, locale, ui);
      else r = await this.runOnce(cmd, locale, ui);

      if (r.status === 'blocked') {
        this.applyDeductFail(r.r, r.needed, r.commandName);
      }
      return r;
    } finally {
      if (this.state) {
        this.state = { ...this.state, isBusy: false };
        this.emit();
      }
    }
  }

  // ======= MSE orchestration =======

  private async enterState(stateId: string) {
    if (!this.state) return;
    this.clearStartTimeout();
    // Candidates for per-iteration variation.
    const candidates = this.getStateCandidates(stateId);
    if (candidates.length === 0) throw new Error(`State has no video: ${stateId}`);
    this.stateVideoCandidates = candidates;
    // Keep lastStateMp4 as-is to avoid immediate repeats when re-entering the same state.

    const pickManifest = () => {
      const mp4 = candidates[Math.floor(Math.random() * candidates.length)] || candidates[0];
      const manifestUrl = mseManifestUrlForMp4(mp4);
      if (!manifestUrl) throw new Error(`Bad state manifest url for ${mp4}`);
      this.lastStateMp4 = mp4;
      return { mp4, manifestUrl };
    };

    const first = pickManifest();

    this.log('state.enter', { stateId, mp4: first.mp4, manifestUrl: first.manifestUrl, variants: candidates.length });
    this.stateManifestUrl = first.manifestUrl;
    this.stateIterEnds = [];
    this.stateIterIndex = 0;
    this.stateTickRunning = false;
    this.stateTickPending = false;

    try {
      // Ensure we are at t=0 before wiping timeline.
      try { this.video.pause(); } catch {}
      try { this.video.currentTime = 0; } catch {}

      // Timeline restart: load first clip, start playback immediately, append second in background.
      await this.mse.removeFrom(0);
      const openTimeoutMs = TUNE.mseOpenTimeoutMs;
      await this.mse.appendClipAt(first.manifestUrl, 0, { openTimeoutMs });
      this.log('mse.impl', { impl: this.mse.implName?.() || 'unknown' });
      const end1 = this.mse.bufferedEnd();
      const d1 = Math.max(0, end1);
      this.stateDurEstimateSec = d1;
      this.stateIterEnd = end1;
      this.stateIterEnds = [end1];
      this.stateIterIndex = 0;
      this.log('state.buffered', {
        stateId,
        dur: d1,
        bufferedEnd: this.mse.bufferedEnd(),
      });

      // Start playback with first clip ready — don't wait for second.
      try { await this.video.play(); } catch {}

      // Append second clip in background for seamless looping.
      if (!this.destroyed) {
        const second = pickManifest();
        this.mse.appendClipAt(second.manifestUrl, end1, { openTimeoutMs })
          .then(() => {
            if (this.destroyed) return;
            const end2 = this.mse.bufferedEnd();
            const d2 = Math.max(0, end2 - end1) || d1;
            this.stateDurEstimateSec = d2 || d1;
            this.stateIterEnds = [end1, end2];
          })
          .catch(() => {});
      }

      this.startBufferingForStateActions(stateId);

      // Keep timeline alive: append more state iters when approaching the end.
      this.installStateLoopTick();

      // Startup hang detection (cheap): if we don't reach 'playing' soon, report once.
      this.armStartTimeout({ stateId, stage: 'enterState' });
    } catch (e) {
      this.log('mse.unavailable', { err: String(e) });
      // Do not auto-fallback to native: we want MMS/MSE to be the primary path on iOS.
      // Propagate the error to surface it in the debug UI.
      throw e;
    }
  }

  private installStateLoopTick() {
    try { this.video.ontimeupdate = null; } catch {}
    const runOnce = async () => {
      if (this.destroyed || !this.state) return;
      // While running a transition/once, avoid racing with timeline surgery.
      if (this.inAction || this.state.isBusy) return;
      if (!this.stateManifestUrl) return;
      // Update iteration boundaries as time passes
      const t = Number(this.video.currentTime || 0);
      while (t >= this.stateIterEnd - TUNE.iterEpsilonSec) {
        const prevEnd = this.stateIterEnd;
        const nextIdx = this.stateIterIndex + 1;
        if (nextIdx < this.stateIterEnds.length) {
          this.stateIterIndex = nextIdx;
          this.stateIterEnd = this.stateIterEnds[nextIdx];
        } else if (this.stateDurEstimateSec > 0) {
          this.stateIterEnd = prevEnd + this.stateDurEstimateSec;
        } else {
          this.stateIterEnd = Math.max(prevEnd, t + TUNE.stateTickFallbackAdvanceSec);
        }
      }

      // Keep at least ~2 iterations buffered ahead (so boundary is always within buffer).
      const end = this.mse.bufferedEnd();
      const itersAhead = Math.max(0, this.stateIterEnds.length - (this.stateIterIndex + 1));
      const needMore =
        itersAhead < TUNE.stateMinItersAhead ||
        end < this.stateIterEnd + Math.max(1, this.stateDurEstimateSec) * TUNE.stateNeedMoreMultiplier;
      if (needMore && this.stateVideoCandidates.length > 0) {
        const appendAt = end;
        try {
          // Pick a random variant for each iteration (true random; repeats are allowed).
          const candidates = this.stateVideoCandidates;
          const mp4 = candidates[Math.floor(Math.random() * candidates.length)] || candidates[0];
          const mu = mseManifestUrlForMp4(mp4);
          if (!mu) return;
          this.lastStateMp4 = mp4;
          this.stateManifestUrl = mu;
          await this.mse.appendClipAt(mu, appendAt);
          const newEnd = this.mse.bufferedEnd();
          const d = Math.max(0, newEnd - appendAt);
          if (d > 0.01) this.stateDurEstimateSec = d;
          const last = this.stateIterEnds.length ? this.stateIterEnds[this.stateIterEnds.length - 1] : 0;
          if (newEnd > last + 0.001) this.stateIterEnds.push(newEnd);
        } catch {}
      }
    };
    const runSerialized = async () => {
      if (this.stateTickRunning) {
        this.stateTickPending = true;
        return;
      }
      this.stateTickRunning = true;
      try {
        // Coalesce bursts of timeupdate: run at least once, then repeat if something queued up.
        do {
          this.stateTickPending = false;
          await runOnce();
        } while (this.stateTickPending && !this.destroyed);
      } finally {
        this.stateTickRunning = false;
      }
    };
    try { this.video.ontimeupdate = () => { void runSerialized(); }; } catch {}
  }

  private nextStateBoundary(): number {
    // Boundary must be strictly ahead of currentTime; otherwise timeline surgery can delete
    // currently playing buffer (Telegram WebView often stalls hard in that case).
    const now = Number(this.video.currentTime || 0);
    let boundary = Number(this.stateIterEnd || 0);
    const d = Number(this.stateDurEstimateSec || 0);
    if (d > 0) {
      // If we are at/after the tracked iteration end (or tick hasn't updated yet), advance.
      // Also ensure some lead time so we can finish appends before hitting the boundary.
      const minLead = TUNE.boundaryMinLeadSec; // seconds
      while (boundary <= now + minLead) {
        // Prefer real boundaries if we have them.
        const nextIdx = this.stateIterIndex + 1;
        if (nextIdx < this.stateIterEnds.length) {
          boundary = this.stateIterEnds[nextIdx];
          break;
        }
        boundary += d;
      }
    } else {
      // Should not happen, but keep a safe future boundary.
      boundary = Math.max(boundary, now + TUNE.boundaryFallbackLeadSec);
    }
    return boundary;
  }

  private log(tag: string, data?: unknown) {
    this.telemetry.event(tag, data);
  }

  private reportOnce(type: string, message: string, meta?: Record<string, any>, severity: 'info' | 'warning' | 'error' = 'error') {
    this.telemetry.errorOnce(type, message, meta, severity);
  }

  private clearStartTimeout() {
    if (!this.startTimeoutTimer) return;
    try { clearTimeout(this.startTimeoutTimer); } catch {}
    this.startTimeoutTimer = null;
  }

  private armStartTimeout(meta: { stateId?: string; stage: string }) {
    this.clearStartTimeout();
    const v = this.video;
    const startedAt = Date.now();
    // If we already have progress, don't arm.
    try {
      if (!v) return;
      if (Number(v.currentTime || 0) > TUNE.iterEpsilonSec) return;
    } catch {}
    const timeoutMs = TUNE.startTimeoutMs;
    this.startTimeoutTimer = setTimeout(() => {
      this.startTimeoutTimer = null;
      try {
        const rs = (() => { try { return Number((v as any).readyState ?? -1); } catch { return -1; } })();
        const ns = (() => { try { return Number((v as any).networkState ?? -1); } catch { return -1; } })();
        const ct = (() => { try { return Number(v.currentTime || 0); } catch { return 0; } })();
        const paused = (() => { try { return Boolean((v as any).paused); } catch { return null; } })();
        const src = (() => { try { return String((v as any).currentSrc || (v as any).src || ''); } catch { return ''; } })();
        const seconds = Math.round((Date.now() - startedAt) / 1000);

        // IMPORTANT: avoid false positives.
        // We used to only clear this timer on `playing`, but on some WebViews `playing` can fire
        // before we arm the timer (or not fire reliably). If playback already progressed, this is
        // not a startup failure.
        if (ct > TUNE.iterEpsilonSec && paused === false) return;

        // Prefer episode-aware incident tracker (no false positives + no double dedup).
        if (this.incidentTracker) {
          this.incidentTracker.onStartupFailure(seconds, { ...meta, readyState: rs, networkState: ns, currentTime: ct, paused, currentSrc: src, bufferedEnd: this.mse?.bufferedEnd?.() });
        } else {
          this.reportOnce(
            'player_start_timeout',
            `Player start timeout after ${seconds}s`,
            { ...meta, readyState: rs, networkState: ns, currentTime: ct, paused, currentSrc: src, bufferedEnd: this.mse?.bufferedEnd?.() },
            'error',
          );
        }
      } catch {}
    }, timeoutMs);
  }

  private installVideoMonitoring() {
    const v = this.video;
    const on = (event: string, fn: any) => {
      try {
        v.addEventListener(event, fn as any);
        this.removeVideoMonitors.push(() => v.removeEventListener(event, fn as any));
      } catch {}
    };

    on('playing', () => {
      // Successful progress: cancel startup timeout.
      this.clearStartTimeout();
      // Start ticking only when playback is active (saves CPU).
      try { this.incidentTracker?.start(); } catch {}
    });
    on('timeupdate', () => {
      // More robust than relying on `playing` only: if currentTime advanced, startup succeeded.
      try {
        if (Number(v.currentTime || 0) > TUNE.iterEpsilonSec) this.clearStartTimeout();
      } catch {}
    });
    on('pause', () => {
      try { this.incidentTracker?.stop(); } catch {}
    });
    on('ended', () => {
      try { this.incidentTracker?.stop(); } catch {}
    });
    on('canplay', () => {
      // canplay means we have enough data to start; still keep timeout if play never happens.
    });
    on('error', () => {
      try {
        const err = (v as any).error;
        const code = Number(err?.code || 0);
        if (this.incidentTracker) {
          this.incidentTracker.onFatalVideoError(code || null, {
            readyState: (v as any).readyState,
            networkState: (v as any).networkState,
            currentTime: Number(v.currentTime || 0),
            currentSrc: String((v as any).currentSrc || (v as any).src || ''),
          });
        } else {
          this.reportOnce(
            'video_error',
            `Video error code=${code || 'unknown'}`,
            {
              code,
              readyState: (v as any).readyState,
              networkState: (v as any).networkState,
              currentTime: Number(v.currentTime || 0),
              currentSrc: String((v as any).currentSrc || (v as any).src || ''),
            },
            'error',
          );
        }
      } catch {}
    });
    on('stalled', () => {
      // Signal only: we confirm real issues via playback progress tracker to avoid false positives.
      try { this.incidentTracker?.noteStalledSignal(); } catch {}
      try { this.incidentTracker?.start(); } catch {}
    });
    on('waiting', () => {
      // Signal only: we confirm real issues via playback progress tracker to avoid false positives.
      try { this.incidentTracker?.noteWaitingSignal(); } catch {}
      try { this.incidentTracker?.start(); } catch {}
    });

    // Flush open incident on pagehide (Telegram WebView often closes quickly).
    try {
      const onHide = () => { try { this.incidentTracker?.flushOnPageHide(); } catch {} };
      window.addEventListener('pagehide', onHide);
      window.addEventListener('beforeunload', onHide);
      this.removeVideoMonitors.push(() => window.removeEventListener('pagehide', onHide));
      this.removeVideoMonitors.push(() => window.removeEventListener('beforeunload', onHide));
    } catch {}
  }

  private async waitUntilOrStall(targetTime: number, opts?: { timeoutMs?: number; stallMs?: number }): Promise<boolean> {
    const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 60000));
    const stallMs = Math.max(300, Number(opts?.stallMs ?? 1500));
    const t0 = performance.now();
    let lastT = Number(this.video.currentTime || 0);
    let lastAdvanceAt = t0;
    let lastLogAt = t0;

    while (!this.destroyed) {
      const now = performance.now();
      const ct = Number(this.video.currentTime || 0);
      if (ct >= targetTime) return true;

      if (Math.abs(ct - lastT) > 0.001) {
        lastT = ct;
        lastAdvanceAt = now;
      }

      if (now - t0 > timeoutMs) {
        this.log('wait.timeout', { ct, targetTime, bufferedEnd: this.mse.bufferedEnd() });
        return false;
      }

      if (now - lastAdvanceAt > stallMs) {
        this.log('wait.stall', { ct, targetTime, bufferedEnd: this.mse.bufferedEnd() });
        return false;
      }

      if (now - lastLogAt > TUNE.waitTickLogEveryMs) {
        lastLogAt = now;
        this.log('wait.tick', { ct, targetTime, bufferedEnd: this.mse.bufferedEnd() });
      }

      await new Promise<void>((r) => requestAnimationFrame(() => r()));
    }
    return false;
  }

  private async recoverToState(stateId: string) {
    // Hard recovery for Telegram WebView stalls: recreate MediaSource blob and re-enter the state.
    this.log('recover.start', { stateId });
    try { this.mse.destroy(); } catch {}
    this.mse = new MseEngine(this.video, {
      onNetRecovered: (stage, meta) => {
        const type = stage === 'manifest' ? 'player_net_recovered_manifest' : 'player_net_recovered_segment';
        this.telemetry.errorOnce(
          type,
          type,
          { feature: 'chat', stage: `mse_${stage}_recovered`, ...(meta || {}) },
          'warning',
        );
      },
    });
    this.stateTickRunning = false;
    this.stateTickPending = false;
    // Reset time to avoid "out of range" on a fresh MediaSource timeline.
    try { this.video.pause(); } catch {}
    try { this.video.currentTime = 0; } catch {}
    await this.enterState(stateId);
    this.log('recover.done', { stateId });
  }

  private async runTransition(cmd: Command, locale: string, ui?: UiCommandContext) {
    if (!this.state) return { status: 'noop' } as const;
    this.inAction = true;
    const { idx } = this.state;
    const fromStateId = String(this.state.currentStateId);
    const modelId = this.state.modelId;
    this.log('transition.start', { fromStateId, cmd: cmd.command });

    const t = getTransitionForCommand(idx, fromStateId, cmd.command);
    if (!t) { this.inAction = false; return { status: 'noop' } as const; }

    let chosenTransitionMp4: string | null = null;
    let transitionCandidates: string[] = [];
    let actionManifestUrl: string | null = null;
    let validTo: string = fromStateId;
    try {
      const bill = await this.chargeForAction({ kind: 'transition', commandName: cmd.command, modelId, cost: Number(t.cost || 0), premiumOnly: cmd.premiumOnly, ui });
      if (!bill.ok) return { status: 'blocked', r: bill.r, needed: bill.needed, commandName: cmd.command, runId: bill.runId ?? null } as const;

      const to = String(t.to || '').trim();
      validTo = to && idx.statesById.has(to) ? to : fromStateId;
      // Transition videos are VARIANTS (pick one), not a playlist.
      // If multiple are selected in editor, we should play ONE random video, not all sequentially.
      const pick = this.pickRandomActionVariant(t as any);
      transitionCandidates = pick.variants;
      chosenTransitionMp4 = pick.chosen;
      const playlist = chosenTransitionMp4 ? [chosenTransitionMp4] : [];
      this.log('transition.plan', {
        validTo,
        variants: transitionCandidates.length,
        chosen: chosenTransitionMp4 || null,
      });

      const nextCandidates = this.getStateCandidates(validTo);
      actionManifestUrl = playlist[0] ? mseManifestUrlForMp4(playlist[0]) : null;
      if (!actionManifestUrl) throw new Error('bad_transition_manifest');

      const out = await this.spliceActionAndEnterTarget({
        tag: 'transition',
        actionManifestUrl: actionManifestUrl,
        targetStateId: validTo,
        targetCandidates: nextCandidates,
      });

      if (!this.state) return { status: 'noop' } as const;
      this.state = { ...this.state, currentStateId: validTo };
      this.persistState();
      this.refreshCommands(locale);
      this.emit();

      // Re-arm loop tracking from that point
      this.stateVideoCandidates = nextCandidates;
      this.lastStateMp4 = out.appended.p2.mp4;
      this.stateManifestUrl = out.appended.p2.mu;
      this.stateDurEstimateSec = out.appended.d2 || out.appended.d1;
      this.stateIterEnd = out.appended.endAfter1;
      this.stateIterEnds = [out.appended.endAfter1, out.appended.endAfter2];
      this.stateIterIndex = 0;
      this.startBufferingForStateActions(validTo);
      this.log('transition.done', { validTo, newStateStart: out.targetStart });
      return {
        status: 'ok',
        runId: bill.runId ?? null,
        spentCost: bill.spentCost,
        accessSource: bill.accessSource || null,
        privateShowGrantId: bill.privateShowGrantId || null,
      } as const;
    } catch (e) {
      // If timeline surgery fails (missing manifest / network hiccup), recover to the current state.
      const cur = this.state ? String(this.state.currentStateId) : fromStateId;
      const tele = (e as any)?.telemetry && typeof (e as any).telemetry === 'object' ? (e as any).telemetry : null;
      this.log('transition.error', {
        cur,
        err: String((e as any)?.message || e || 'transition_failed'),
        stage: tele?.stage || null,
        status: tele?.status ?? null,
      });
      try { await this.recoverToState(cur); } catch {}
      return {
        status: 'error',
        err: 'transition_failed',
        cause: e,
        meta: {
          action_kind: 'transition',
          command: String(cmd?.command || ''),
          from_state_id: fromStateId,
          to_state_id: validTo,
          chosen_mp4: chosenTransitionMp4 || null,
          action_manifest_url: actionManifestUrl || null,
          target_candidates_count: Array.isArray(transitionCandidates) ? transitionCandidates.length : null,
          mse_impl: this.mse?.implName?.() || null,
          current_time: Number(this.video.currentTime || 0) || 0,
          buffered_end: this.mse?.bufferedEnd?.() ?? null,
          telemetry: tele || null,
        },
      } as const;
    } finally {
      this.inAction = false;
    }
  }

  private async runOnce(cmd: Command, locale: string, ui?: UiCommandContext) {
    if (!this.state) return { status: 'noop' } as const;
    this.inAction = true;
    const { idx } = this.state;
    const fromStateId = String(this.state.currentStateId);
    const modelId = this.state.modelId;
    this.log('once.start', { fromStateId, cmd: cmd.command });

    const o = getOnceForCommand(idx, fromStateId, cmd.command);
    if (!o) { this.inAction = false; return { status: 'noop' } as const; }

    let chosenOnceMp4: string | null = null;
    let onceCandidates: string[] = [];
    let onceManifest: string | null = null;
    try {
      const bill = await this.chargeForAction({ kind: 'once', commandName: cmd.command, modelId, cost: Number(o.cost || 0), premiumOnly: cmd.premiumOnly, ui });
      if (!bill.ok) return { status: 'blocked', r: bill.r, needed: bill.needed, commandName: cmd.command, runId: bill.runId ?? null } as const;

      // Once videos are VARIANTS (pick one), same logic as transitions.
      const pick = this.pickRandomActionVariant(o as any);
      onceCandidates = pick.variants;
      chosenOnceMp4 = pick.chosen;
      if (!chosenOnceMp4) return { status: 'noop' } as const;
      onceManifest = mseManifestUrlForMp4(chosenOnceMp4);
      if (!onceManifest) return { status: 'noop' } as const;
      this.log('once.plan', { variants: onceCandidates.length, chosen: chosenOnceMp4 || null, onceManifest });

      const out = await this.spliceActionAndEnterTarget({
        tag: 'once',
        actionManifestUrl: onceManifest,
        targetStateId: fromStateId,
        targetCandidates: this.stateVideoCandidates,
      });

      // Re-arm loop tracking (same state)
      this.lastStateMp4 = out.appended.p2.mp4;
      this.stateManifestUrl = out.appended.p2.mu;
      this.stateDurEstimateSec = out.appended.d2 || out.appended.d1;
      this.stateIterEnds = [out.appended.endAfter1, out.appended.endAfter2];
      this.stateIterIndex = 0;
      this.stateIterEnd = out.appended.endAfter1;

      this.refreshCommands(locale);
      this.emit();
      this.log('once.done', { returnStart: out.targetStart });
      return {
        status: 'ok',
        runId: bill.runId ?? null,
        spentCost: bill.spentCost,
        accessSource: bill.accessSource || null,
        privateShowGrantId: bill.privateShowGrantId || null,
      } as const;
    } catch (e) {
      const cur = this.state ? String(this.state.currentStateId) : fromStateId;
      const tele = (e as any)?.telemetry && typeof (e as any).telemetry === 'object' ? (e as any).telemetry : null;
      this.log('once.error', {
        cur,
        err: String((e as any)?.message || e || 'once_failed'),
        stage: tele?.stage || null,
        status: tele?.status ?? null,
      });
      try { await this.recoverToState(cur); } catch {}
      return {
        status: 'error',
        err: 'once_failed',
        cause: e,
        meta: {
          action_kind: 'once',
          command: String(cmd?.command || ''),
          from_state_id: fromStateId,
          chosen_mp4: chosenOnceMp4 || null,
          action_manifest_url: onceManifest || null,
          candidates_count: Array.isArray(onceCandidates) ? onceCandidates.length : null,
          mse_impl: this.mse?.implName?.() || null,
          current_time: Number(this.video.currentTime || 0) || 0,
          buffered_end: this.mse?.bufferedEnd?.() ?? null,
          telemetry: tele || null,
        },
      } as const;
    } finally {
      this.inAction = false;
    }
  }

  // ======= Buffer policy (network warm-up) =======

  private startBufferingForStateActions(stateId: string) {
    if (!this.state) return;
    const loc = this.deps.locale();
    const avail = availableCommands(this.state.idx, stateId, loc);
    for (const c of avail) {
      if (c.kind === 'transition') {
        const t = getTransitionForCommand(this.state.idx, stateId, c.command);
        // Warm up only one variant (best-effort). Real pick happens at action time.
        this.prefetchFirstActionVariant(t as any);
      } else {
        const o = getOnceForCommand(this.state.idx, stateId, c.command);
        // Warm up only one variant (best-effort). Real pick happens at action time.
        this.prefetchFirstActionVariant(o as any);
      }
    }
  }

  private prefetchStateManifest(stateId: string) {
    if (!this.state) return;
    const mp4 = this.getPrefetchMp4ForState(stateId);
    if (!mp4) return;
    const mu = mseManifestUrlForMp4(mp4);
    if (mu) void this.mse.prefetchClip(mu).catch((e) => this.log('prefetch.error', { mu, err: String(e) }));
  }

  // ======= UI state helpers =======

  private refreshCommands(locale?: string) {
    if (!this.state) return;
    const loc = locale || this.deps.locale();
    const i18nT = this.deps.services.i18n.t.bind(this.deps.services.i18n);
    const avail = availableCommands(this.state.idx, this.state.currentStateId, loc, i18nT);
    const availKeys = new Set(avail.map((c) => `${c.kind}:${c.command}`));
    const all = allCommands(this.state.idx, loc, i18nT);
    this.state = {
      ...this.state,
      commands: all.map((c) => ({ ...c, available: availKeys.has(`${c.kind}:${c.command}`) })),
      purchasedCommands: this.billing.listPurchased(),
    };
  }

  private emit() {
    if (!this.state || this.destroyed) return;
    this.deps.onState(this.state);
  }

  private persistState() {
    if (!this.state) return;
    persistStateId(this.state.modelId, this.state.currentStateId);
  }

  private applyDeductFail(r: DeductResult, needed: number, commandName: string) {
    if (!this.state) return;
    if (r.ok) return;
    if (r.reason === 'insufficient') {
      // Do not touch isBusy here; execute() owns finalization of busy state.
      this.state = { ...this.state, paywall: { open: true, reason: 'insufficient_credits', balance: r.balance ?? null, needed, commandName } };
      this.emit();
      return;
    }
    if ((r as any).reason === 'premium_required') {
      // Premium-only command attempted by a FREE user. Let the chat agent respond first;
      // UI decides when to show the paywall (e.g. after model reply typing finishes).
      this.state = { ...this.state, paywall: { open: true, reason: 'premium_required', balance: null, needed: 0, commandName } };
      this.emit();
      return;
    }
    if ((r as any).reason === 'private_show_required') {
      this.state = {
        ...this.state,
        paywall: {
          open: true,
          reason: 'private_show_required',
          balance: null,
          needed: Math.max(0, Number((r as any).required ?? needed) || 0),
          commandName,
          private_show: (r as any).private_show || null,
        },
      };
      this.emit();
      return;
    }
    if (r.reason === 'auth') {
      const tg = this.deps.services.telegram.tg;
      const inTelegram = !!tg;
      if (inTelegram && typeof tg?.showPopup === 'function') {
        try {
          const t = this.deps.services.i18n.t.bind(this.deps.services.i18n);
          tg.showPopup({
            title: t('auth.required_title', 'Authorization required'),
            message: t('auth.required_message', 'Please close and reopen the mini app from Telegram.'),
            buttons: [{ type: 'ok' }],
          });
          return;
        } catch {
          // fall through
        }
      }
      // Outside Telegram (or popup unavailable): show our overlay path.
      try {
        const path = `${window.location.pathname}${window.location.search || ''}`;
        requestAuthRequired({ path });
      } catch {}
    }
    // For 'auth'/'network' we just let execute() clear busy; optional UI is handled above.
  }
}
